<template>
    <v-card outlined class="fc" :class="{ 'fc--closed': !votable }">
        <div class="d-flex pa-4 pa-sm-5">
            <div class="fc-vote-col">
                <button type="button" class="fc-vote" :class="{ 'fc-vote--on': item.voted }" :disabled="!votable || voting" :aria-pressed="item.voted ? 'true' : 'false'" :aria-label="voteLabel" :title="voteLabel" @click="$emit('vote', item)">
                    <v-progress-circular v-if="voting" indeterminate size="18" width="2" aria-hidden="true" />
                    <v-icon v-else size="22" aria-hidden="true">mdi-arrow-up-bold</v-icon>
                    <span class="fc-vote-count" aria-live="polite">{{ item.votes }}</span>
                </button>
            </div>

            <div class="fc-main">
                <div class="d-flex flex-wrap align-center fc-meta">
                    <v-chip small label outlined :color="typeMeta.color" class="mr-2 mb-1"><v-icon small left>{{ typeMeta.icon }}</v-icon>{{ typeMeta.label }}</v-chip>
                    <v-chip v-if="showStatus || item.is_mine" small :color="statusMeta.color" text-color="white" class="mr-2 mb-1"><v-icon small left>{{ statusMeta.icon }}</v-icon>{{ statusMeta.label }}</v-chip>
                    <span v-if="item.suite" class="fc-suite mr-2 mb-1">{{ suiteLabel }}</span>
                    <span v-if="item.is_mine" class="fc-suite fc-mine mr-2 mb-1"><v-icon x-small left color="primary">mdi-account-check-outline</v-icon>Your feedback</span>
                    <v-spacer />
                    <span class="fc-date mb-1">{{ formattedDate }}</span>
                </div>

                <h3 class="fc-title">{{ item.title }}</h3>
                <p class="fc-description mb-0" :class="{ 'fc-clamped': !expanded }">{{ item.description }}</p>
                <a v-if="item.description.length > 220" href="#" class="fc-more" @click.prevent="expanded = !expanded">{{ expanded ? "Show less" : "Show more" }}</a>

                <FeedbackAttachments v-if="item.attachments && item.attachments.length" :item-id="item.id" :attachments="item.attachments" class="mt-3" />

                <div v-if="item.admin_note" class="fc-reply mt-3" :class="{ 'fc-reply--rejected': item.status === 'rejected' }">
                    <div class="fc-reply-label"><v-icon x-small left aria-hidden="true">mdi-reply</v-icon>Reply from Quaro</div>
                    <div>{{ item.admin_note }}</div>
                </div>
                <p v-if="item.status === 'pending'" class="fc-note mt-3 mb-0"><v-icon x-small left>mdi-clock-outline</v-icon>Under review. It will appear in the hub once we have approved it.</p>
                <p v-if="item.status === 'merged'" class="fc-note mt-3 mb-0">
                    <v-icon x-small left>mdi-call-merge</v-icon>Combined with a similar suggestion<span v-if="item.merged_into_title">: “{{ item.merged_into_title }}”</span>. All votes are counted there.
                </p>

                <div v-if="admin && item.status !== 'pending'" class="mt-3">
                    <v-btn x-small text rounded color="primary" @click="$emit('show-votes', item)"><v-icon x-small left>mdi-domain</v-icon>Details and votes by company</v-btn>
                </div>
            </div>
        </div>
    </v-card>
</template>

<script>
    import FeedbackAttachments from "./FeedbackAttachments.vue"
    import { TYPE_META, STATUS_META, SUITE_LABELS, VOTABLE_STATUSES } from "./feedbackMeta"

    export default {
        name: "FeedbackItemCard",
        components: { FeedbackAttachments },
        props: {
            item: { type: Object, required: true },
            admin: { type: Boolean, default: false },
            voting: { type: Boolean, default: false },
            // In der Liste "Open for voting" ist der Status "Open" selbstverstaendlich und wird ausgeblendet
            showStatus: { type: Boolean, default: true }
        },
        data() {
            return { expanded: false }
        },
        computed: {
            typeMeta() {
                return TYPE_META[this.item.type] || TYPE_META.feature
            },
            statusMeta() {
                return STATUS_META[this.item.status] || STATUS_META.open
            },
            suiteLabel() {
                return SUITE_LABELS[this.item.suite] || this.item.suite
            },
            votable() {
                return VOTABLE_STATUSES.includes(this.item.status)
            },
            voteLabel() {
                if (!this.votable) return "Voting is closed for this item"
                return this.item.voted ? "Remove your vote" : "Vote for this idea"
            },
            formattedDate() {
                const date = new Date(this.item.created_at)
                return isNaN(date.getTime()) ? "" : date.toLocaleDateString()
            }
        }
    }
</script>

<style scoped>
    .fc.v-card {
        border-radius: 16px;
        border-color: rgba(128, 128, 128, 0.25);
        transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
    }
    .fc.v-card:hover {
        border-color: var(--v-primary-base);
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    }
    .fc-vote-col {
        margin-right: 16px;
    }
    .fc-vote {
        width: 56px;
        height: 68px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        border: 1px solid rgba(128, 128, 128, 0.4);
        border-radius: 14px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    }
    .fc-vote:hover:not(:disabled) {
        border-color: var(--v-primary-base);
        color: var(--v-primary-base);
    }
    .fc-vote:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 2px;
    }
    .fc-vote:disabled {
        cursor: default;
        opacity: 0.55;
    }
    .fc-vote--on,
    .fc-vote--on:hover:not(:disabled) {
        background: var(--v-primary-base);
        border-color: var(--v-primary-base);
        color: white;
    }
    .fc-vote-count {
        font-weight: 700;
        font-size: 16px;
        line-height: 1;
    }
    .fc-main {
        min-width: 0;
        flex: 1;
    }
    .fc-suite {
        font-size: 12px;
        opacity: 0.7;
    }
    .fc-mine {
        font-weight: 600;
        opacity: 1;
    }
    .fc-date {
        font-size: 12px;
        opacity: 0.6;
    }
    .fc-title {
        font-family: "CalSans", sans-serif;
        font-weight: normal;
        font-size: 18px;
        line-height: 1.3;
        word-break: break-word;
    }
    .fc-description {
        margin-top: 4px;
        white-space: pre-line;
        word-break: break-word;
        font-size: 14px;
        line-height: 1.5;
    }
    .fc-clamped {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .fc-more {
        font-size: 13px;
        font-weight: 600;
    }
    .fc-reply {
        position: relative;
        padding: 10px 14px;
        border-radius: 12px;
        font-size: 14px;
        overflow: hidden;
    }
    .fc-reply::before {
        content: "";
        position: absolute;
        inset: 0;
        background: var(--v-primary-base);
        opacity: 0.09;
        pointer-events: none;
    }
    .fc-reply--rejected::before {
        background: var(--v-error-base);
    }
    .fc-reply-label {
        font-size: 12px;
        font-weight: 700;
        margin-bottom: 2px;
        color: var(--v-primary-base);
    }
    .fc-reply--rejected .fc-reply-label {
        color: var(--v-error-base);
    }
    .fc-note {
        font-size: 13px;
        opacity: 0.75;
    }
    @media (max-width: 599px) {
        .fc-vote-col {
            margin-right: 12px;
        }
        .fc-vote {
            width: 48px;
            height: 60px;
        }
    }
</style>
